import type { AiExplanation } from "./engine/ai";
import type { Suit } from "./engine/types";

export type Locale = "en" | "zh" | "ja";

const translations = {
  en: {
    brandSub: "MIDNIGHT PARTNERSHIP",
    quick: "QUICK MATCH",
    daily: "DAILY DEAL",
    coach: "COACH",
    rules: "RULES",
    fullRules: "FULL RULES & STRATEGY",
    sound: "SOUND",
    on: "ON",
    off: "OFF",
    we: "WE",
    they: "THEY",
    target: "TARGET",
    you: "You",
    cards: "cards",
    dealer: "Dealer",
    maker: "Maker",
    trump: "Trump",
    candidate: "Trump candidate",
    noneYet: "None yet",
    round: "Round",
    callRound1: "FIRST CALLING ROUND",
    callRound2: "SECOND CALLING ROUND",
    yourCall: "You are making the call.",
    pass: "PASS",
    orderUp: "ORDER UP",
    alone: "GO ALONE",
    call: "CALL",
    mustChoose: "Stick the Dealer — choose a suit.",
    discard: "Choose one card to discard.",
    yourTurn: "Your turn — choose a legal card.",
    thinking: "is thinking…",
    collecting: "Trick complete",
    nextHand: "DEAL NEXT HAND",
    newMatch: "NEW MATCH",
    continue: "CONTINUE GAME",
    resume: "Resume your saved match",
    coachInsight: "COACH INSIGHT",
    showHint: "SHOW COACH TIP",
    hideHint: "HIDE TIP",
    handComplete: "HAND COMPLETE",
    matchComplete: "MATCH COMPLETE",
    share: "SHARE RESULT",
    close: "CLOSE",
    settings: "SETTINGS",
    reducedMotion: "Reduced motion",
    confirmPlay: "Confirm card before play",
    clearData: "CLEAR LOCAL DATA",
    clearConfirm: "Clear every saved match, setting and local statistic? This cannot be undone.",
    restartConfirm: "Abandon this match and start again?",
    restartTitle: "Start a new match?",
    startOver: "START OVER",
    cancel: "CANCEL",
    assisted: "Assisted",
    unassisted: "Clean",
    stats: "YOUR TABLE RECORD",
    wins: "Wins",
    games: "Games",
    winRate: "Win rate",
    hands: "Hands",
    callSuccess: "Call success",
    euchresLabel: "Euchres",
    aloneSuccess: "Alone success",
    averageHands: "Average hands",
    streak: "Daily streak",
    latestDaily: "Today’s Daily Deal",
    noDaily: "Today’s Daily Deal is not completed.",
    dailyWon: "Won",
    dailyLost: "Lost",
    marginLabel: "Margin",
    resultSummary: "Match performance",
    playDaily: "PLAY DAILY DEAL",
    heroEyebrow: "CALL IT RIGHT. TAKE THREE.",
    gameModes: "Game modes",
    tableUtilities: "Table utilities",
    language: "Language",
    playableTable: "Playable Euchre table",
    yourHand: "Your hand",
    scoreAria: "Score",
    actionUnavailable: "That action is unavailable now.",
    coachOrder: "Order up {suit}: your hand has enough trump power.",
    coachPass: "Pass now to preserve options for the second calling round.",
    coachCall: "Call {suit}: it is your strongest available trump suit.",
    coachWait: "Watch the public trick while the other seats act.",
    coachFollow: "Follow the effective lead suit with {rank} of {suit}; the recommended card is highlighted.",
    coachBalance: "Play {rank} to balance winning this trick with protecting stronger cards.",
    coachObserve: "Watch the calling order and your partner’s public plays.",
    logStarted: "Match started",
    logRound2: "Second calling round began",
    logPassed: "{seat} passed",
    logOrdered: "{seat} ordered up {suit}{alone}",
    logCalled: "{seat} called {suit}{alone}",
    logDiscarded: "{seat} discarded one card",
    logPlayed: "{seat} played {card}",
    logAlone: " alone",
    howTitle: "How to play Euchre",
    howBody:
      "Twenty-four cards, four seats and two partnerships. Call trump, take at least three of five tricks and be the first team to 10 points.",
    bowerTitle: "Bowers change the suit",
    bowerBody:
      "The trump Jack is the Right Bower. The other Jack of the same colour is the Left Bower and counts as trump—not its printed suit.",
    callingTitle: "Two calling rounds",
    callingBody:
      "Round one can order up the upcard suit. Round two can call any other suit. If everyone passes again, the dealer must choose.",
    scoreTitle: "Fixed scoring",
    scoreBody:
      "Makers take 3–4 tricks for 1 point or all 5 for 2. Defenders earn 2 for a euchre. A lone sweep earns 4.",
    strategyTitle: "Partner-first strategy",
    strategyBody:
      "Track public trump, avoid overtaking a winning partner without reason and lead strength when it can draw opposing trump.",
    faqTitle: "Table questions",
    faq1: "Is this four-player Euchre?",
    faqa1: "Yes. You and North are partners against West and East, all with honest seat-scoped AI.",
    faq2: "Can I play offline?",
    faqa2: "Yes. After the first visit, the game shell and local save work offline.",
    faq3: "Which variants are included?",
    faqa3: "Standard 24-card partnership Euchre with Stick the Dealer. Jokers, 32-card decks and regional variants are excluded.",
    legalReason: "You must follow the effective lead suit while you hold it.",
    leftBadge: "LEFT • TRUMP",
    rightBadge: "RIGHT • TRUMP",
    currentTrick: "CURRENT TRICK",
    tricks: "Tricks",
    gameLog: "GAME LOG",
    helpTitle: "Rules & table controls",
    helpText:
      "Call trump in two rounds. Follow the effective lead suit. Right Bower ranks first; Left Bower ranks second and belongs to trump. Arrow keys move through your hand, Space selects and Enter plays.",
    made: "Makers took three or four tricks.",
    march: "Makers swept all five tricks.",
    euchred: "Defenders euchred the makers.",
    "lone-made": "The lone maker took three or four tricks.",
    "lone-march": "The lone maker swept for four points.",
    teamWon: "Your partnership reached 10.",
    teamLost: "The opponents reached 10.",
    dailyHandWon: "Your partnership took the daily hand.",
    dailyHandLost: "The opponents took the daily hand.",
    difficulty: "AI DIFFICULTY",
    difficultyRelaxed: "Relaxed",
    difficultyStandard: "Standard",
    difficultySharp: "Sharp",
    difficultyRelaxedDesc: "Forgiving table: calls cautiously and occasionally slips to a second-best card.",
    difficultyStandardDesc: "The classic balanced table AI.",
    difficultySharpDesc: "Counts played trump, draws trump with the boss card and reads the turned-down card.",
    bowerRight: "Right Bower",
    bowerLeft: "Left Bower",
    bowerIncl: ", incl. {bowers}",
    callFacts: "If {suit} is trump: {trumpCount} trump in your hand{bowers}.",
    suitFactShort: "{trumpCount} trump{bowers}",
    legalReasonFollow: "Must follow {suit} — you still hold {count}.",
    expOrder: "Ordered up {suit}: {trumpCount} trump in hand{bowers}, {offAces} outside aces, strength {score} vs threshold {threshold}.",
    expCall: "Called {suit}: {trumpCount} trump{bowers}, {offAces} outside aces, strength {score} vs threshold {threshold}.",
    expPass: "Passed on {suit}: {trumpCount} trump{bowers}, strength {score} below threshold {threshold}.",
    expDiscard: "Discards the weakest card once {suit} becomes trump.",
    expLead: "Leads {card} with {trumpCount} trump in hand{drawTrump}.",
    expLeadDraw: "; the boss trump draws out the opponents' trump",
    expFollow: "Follows {leadSuit} with {card}, trying to win the trick.",
    expTrump: "Trumps in with {card} to beat {leadSuit}.",
    expDuck: "Plays low with {card}{reason}.",
    expDuckPartner: " — partner is already winning this trick",
    expDuckBeat: " — it cannot beat the current best card",
    coachExpOrder: "Recommendation — order up {suit}: {trumpCount} trump in hand{bowers}, {offAces} outside aces, strength {score} vs threshold {threshold}.",
    coachExpCall: "Recommendation — call {suit}: {trumpCount} trump{bowers}, {offAces} outside aces, strength {score} vs threshold {threshold}.",
    coachExpPass: "Recommendation — pass on {suit}: {trumpCount} trump{bowers}, strength {score} below threshold {threshold}.",
    coachExpDiscard: "Recommendation — discard your weakest card once {suit} becomes trump.",
    coachExpLead: "Recommendation — lead {card} with {trumpCount} trump in hand{drawTrump}.",
    coachExpFollow: "Recommendation — follow {leadSuit} with {card}, trying to win the trick.",
    coachExpTrump: "Recommendation — trump in with {card} to beat {leadSuit}.",
    coachExpDuck: "Recommendation — play low with {card}{reason}.",
    dailyUnavailable: "Today’s Daily Deal is not available in this version — Quick Match and Coach play are ready.",
    dailyReviewTitle: "DAILY REVIEW",
    dailyFocus: "Today's focus: {kind}.",
    kindOrder: "the order-up decision",
    kindPass: "passing now to call later",
    kindAlone: "the going-alone judgment",
    dailyRecommended: "Recommended call: {action}.",
    dailyYourCall: "Your call: {action} — {verdict}.",
    dailyMatched: "matched the recommendation",
    dailyMissed: "differed from the recommendation",
    dailySimFacts: "Simulated expected points this hand — order {order}, pass {pass}, alone {alone}.",
    dailySimNote:
      "The recommendation comes from a fixed budget of deterministic AI playouts ({simulations} per candidate), not from perfect play.",
    actOrder: "order up {suit}",
    actOrderAlone: "order up {suit} alone",
    actCall: "call {suit}",
    actCallAlone: "call {suit} alone",
    actPass: "pass",
    dailyCallLabel: "Call vs recommendation",
    callMatchedShort: "matched",
    callMissedShort: "differed",
    swingTitle: "KEY TURNS",
    swingOwnEuchred: "Hand {n}: your call was euchred — {points} points to the opponents. A thin trump call is the costliest mistake.",
    swingOppEuchred: "Hand {n}: you euchred the opponents' call — {points} points your way. Defending a weak call pays double.",
    swingOwnMarch: "Hand {n}: your partnership swept all five tricks for {points} points. Strong trump plus control turns a call into a march.",
    swingOppMarch: "Hand {n}: the opponents swept all five tricks for {points} points. Count the trump before the hand runs away.",
    swingOwnLoneMarch: "Hand {n}: your lone call swept for {points} points — the biggest play at the table.",
    swingOppLoneMarch: "Hand {n}: an opposing lone call swept for {points} points. Both bowers plus a long suit are unstoppable.",
    swingOwnLoneFail: "Hand {n}: your lone call was euchred — {points} points to the opponents. Going alone needs near-certain tricks.",
    swingOppLoneFail: "Hand {n}: the opponents' lone call was euchred — {points} points your way. Punishing a greedy alone pays double.",
    entryEyebrow: "FIRST TIME AT THIS TABLE?",
    entryTitle: "Choose how to sit down",
    entryLead: "Three ways in — pick one and the table opens.",
    entryTutorialTitle: "Quick tutorial",
    entryTutorialDesc: "New to Euchre? Learn calling and playing in about 3 minutes on a fixed practice hand.",
    entryQuickTitle: "Quick match",
    entryQuickDesc: "Sit in directly — first partnership to 10 points wins.",
    entryDailyTitle: "Daily deal",
    entryDailyDesc: "Today's single decisive calling challenge.",
    tutorialTag: "TUTORIAL",
    tutProgress: "Step {n} of 6",
    tutNext: "NEXT",
    tutExit: "EXIT TUTORIAL",
    tutStartMatch: "START A REAL MATCH",
    tut0Title: "Meet your partner",
    tut0Body:
      "Your partner is Riley (North), across the table; together you face Jordan and Casey. Call trump well and take at least three of the five tricks.",
    tut1Title: "Trump and the bowers",
    tut1Body:
      "The face-up card is the trump candidate. The Jack of trump (Right Bower) is the highest card; the other Jack of the same colour (Left Bower) also counts as trump, not its printed suit.",
    tut2Title: "Make your call",
    tut2Body:
      "Order up to make the candidate suit trump, or pass to wait for round two. Going alone benches your partner for a shot at 4 points. Any choice works — watch what it does.",
    tut2Passed: "You passed — the call moves on. In round two you may name any other suit.",
    tut3Title: "Follow the lead suit",
    tut3Body:
      "You must follow the effective lead suit while you hold it; if you lead, any card is legal. Legal cards stay highlighted in your hand — remember the Left Bower belongs to trump.",
    tut3AfterOrder: "You ordered up {suit} — your side must take at least 3 tricks or be euchred.",
    tut3AfterCall: "You called {suit} — your side must take at least 3 tricks.",
    tut3AfterPass: "Someone else set trump — defend: stop the makers from taking 3 tricks.",
    tut4Title: "Winning a trick",
    tut4Body:
      "The highest trump wins the trick; with no trump played, the highest card of the lead suit wins. The top bar tracks tricks and score — first partnership to 10 points.",
    tut4Winner: "{seat} took the first trick.",
    tut5Title: "Tutorial complete",
    tut5Body:
      "Scoring: makers earn 1 point for 3–4 tricks, 2 for all five (march), 4 for a lone sweep; defenders earn 2 for a euchre. You just called trump and played a legal card — you are ready for a real match.",
    tutQuiet: "Finish this hand — the scoring summary appears at the end.",
  },
  zh: {
    brandSub: "午夜搭档牌桌", quick: "快速对局", daily: "每日牌局", coach: "教练", rules: "规则", fullRules: "完整规则与策略",
    sound: "声音", on: "开", off: "关", we: "我方", they: "对方", target: "目标", you: "你",
    cards: "张牌", dealer: "庄家", maker: "叫主者", trump: "主牌", candidate: "候选主牌", noneYet: "尚未产生",
    round: "轮", callRound1: "第一轮叫主", callRound2: "第二轮叫主", yourCall: "轮到你叫主。",
    pass: "过", orderUp: "接受翻牌", alone: "单打", call: "叫", mustChoose: "庄家必叫——请选择花色。",
    discard: "请选择一张牌弃掉。", yourTurn: "轮到你——请选择合法牌。", thinking: "正在思考…",
    collecting: "本墩结束", nextHand: "发下一手", newMatch: "新对局", continue: "继续已存对局",
    resume: "继续你的已存对局", coachInsight: "教练提示", showHint: "显示提示", hideHint: "隐藏提示",
    handComplete: "本手结束", matchComplete: "比赛结束", share: "分享结果", close: "关闭", settings: "设置",
    reducedMotion: "减少动态效果", confirmPlay: "出牌前二次确认", clearData: "清空本机数据",
    clearConfirm: "清空全部存档、设置和本机统计？此操作不可恢复。", restartConfirm: "放弃当前比赛并重新开始？",
    restartTitle: "开始新对局？", startOver: "重新开始", cancel: "取消",
    assisted: "使用过辅助", unassisted: "Clean（未使用辅助）", stats: "你的牌桌记录", wins: "胜场", games: "场次",
    winRate: "胜率", hands: "手数", callSuccess: "叫主成功率", euchresLabel: "反制次数",
    aloneSuccess: "单打成功率", averageHands: "平均手数", streak: "每日连续",
    latestDaily: "今日每日牌局", noDaily: "今天的每日牌局尚未完成。", dailyWon: "胜", dailyLost: "负",
    marginLabel: "分差", resultSummary: "本场表现", playDaily: "开始每日牌局",
    heroEyebrow: "叫准主牌，拿下三墩。", gameModes: "游戏模式", tableUtilities: "牌桌工具",
    language: "语言", playableTable: "可操作的尤克牌桌", yourHand: "你的手牌", scoreAria: "比分",
    actionUnavailable: "当前无法执行该操作。",
    coachOrder: "接受 {suit}：你的主牌实力足够。",
    coachPass: "现在选择过，可以为第二轮叫主保留更多选择。",
    coachCall: "叫 {suit}：这是你当前最强的可选主牌花色。",
    coachWait: "其他座位行动时，留意桌面上已公开的牌。",
    coachFollow: "用 {rank}{suit} 跟随有效首出花色；建议牌已高亮。",
    coachBalance: "建议出 {rank}，兼顾赢下本墩和保留更强的牌。",
    coachObserve: "留意叫主顺序以及搭档已经公开的出牌。",
    logStarted: "比赛开始", logRound2: "进入第二轮叫主", logPassed: "{seat} 选择过",
    logOrdered: "{seat} 接受 {suit}{alone}", logCalled: "{seat} 叫 {suit}{alone}",
    logDiscarded: "{seat} 弃掉一张牌", logPlayed: "{seat} 打出 {card}", logAlone: "并单打",
    howTitle: "尤克牌怎么玩",
    howBody: "使用 24 张牌、四个座位和两组搭档。叫定主牌，在五墩中至少赢三墩，率先得到 10 分。",
    bowerTitle: "Bower 会改变有效花色",
    bowerBody: "主牌 J 是 Right Bower；同色另一张 J 是 Left Bower，也算主牌，不再属于牌面印刷花色。",
    callingTitle: "两轮叫主", callingBody: "第一轮只能接受翻牌花色；第二轮可叫其他花色。若仍全过，庄家必须选择。",
    scoreTitle: "固定计分", scoreBody: "叫主队赢 3–4 墩得 1 分、全赢得 2 分；被反制时守方得 2 分；单打全赢得 4 分。",
    strategyTitle: "搭档优先策略", strategyBody: "记录已公开主牌；搭档领先时不要无故盖牌；需要逼出对方主牌时主动领强牌。",
    faqTitle: "常见问题", faq1: "这是四人尤克牌吗？", faqa1: "是。你和北家搭档，对抗西家与东家；AI 只看到各自应见的信息。",
    faq2: "可以离线玩吗？", faqa2: "可以。首次访问后，游戏和本机存档可离线使用。",
    faq3: "包含哪些变体？", faqa3: "采用 24 张标准搭档玩法和庄家必叫；不含 Joker、32 张牌或地区变体。",
    legalReason: "只要手中还有首出有效花色，就必须跟该花色。", leftBadge: "LEFT · 主牌",
    rightBadge: "RIGHT · 主牌", currentTrick: "当前一墩", tricks: "墩", gameLog: "牌局记录",
    helpTitle: "规则与操作", helpText: "两轮叫主；必须跟有效首出花色。Right 最高，Left 第二且属于主牌。方向键移动手牌，空格选择，回车出牌。",
    made: "叫主队赢得三或四墩。", march: "叫主队横扫五墩。", euchred: "守方成功反制叫主队。",
    "lone-made": "单打叫主者赢得三或四墩。", "lone-march": "单打横扫，获得四分。",
    teamWon: "你的搭档队率先达到 10 分。", teamLost: "对手率先达到 10 分。",
    dailyHandWon: "你的搭档队赢下了每日一手。", dailyHandLost: "对手赢下了每日一手。",
    difficulty: "AI 难度",
    difficultyRelaxed: "休闲", difficultyStandard: "标准", difficultySharp: "锐利",
    difficultyRelaxedDesc: "更宽容的对手：叫主更谨慎，偶尔会出次优牌。",
    difficultyStandardDesc: "经典均衡的牌桌 AI。",
    difficultySharpDesc: "会计算已出主牌、用最大主牌逼主，并参考被翻下的牌。",
    bowerRight: "Right Bower", bowerLeft: "Left Bower", bowerIncl: "，含 {bowers}",
    callFacts: "若 {suit} 为主牌：你手中有 {trumpCount} 张主牌{bowers}。",
    suitFactShort: "{trumpCount} 张主牌{bowers}",
    legalReasonFollow: "必须跟 {suit}——你还持有 {count} 张。",
    expOrder: "接受 {suit}：手中有 {trumpCount} 张主牌{bowers}、{offAces} 张旁门 A，实力 {score}，阈值 {threshold}。",
    expCall: "叫 {suit}：{trumpCount} 张主牌{bowers}、{offAces} 张旁门 A，实力 {score}，阈值 {threshold}。",
    expPass: "放弃 {suit}：仅 {trumpCount} 张主牌{bowers}，实力 {score} 低于阈值 {threshold}。",
    expDiscard: "{suit} 定主后弃掉最弱的一张牌。",
    expLead: "领出 {card}，手中还有 {trumpCount} 张主牌{drawTrump}。",
    expLeadDraw: "；最大主牌可逼出对手的主牌",
    expFollow: "用 {card} 跟 {leadSuit}，尝试赢下本墩。",
    expTrump: "用 {card} 将吃，压过 {leadSuit}。",
    expDuck: "垫出小牌 {card}{reason}。",
    expDuckPartner: "——搭档已经领先本墩",
    expDuckBeat: "——无法压过当前最大牌",
    coachExpOrder: "建议接受 {suit}：手中有 {trumpCount} 张主牌{bowers}、{offAces} 张旁门 A，实力 {score}，阈值 {threshold}。",
    coachExpCall: "建议叫 {suit}：{trumpCount} 张主牌{bowers}、{offAces} 张旁门 A，实力 {score}，阈值 {threshold}。",
    coachExpPass: "建议放弃 {suit}：仅 {trumpCount} 张主牌{bowers}，实力 {score} 低于阈值 {threshold}。",
    coachExpDiscard: "建议在 {suit} 定主后弃掉最弱的一张牌。",
    coachExpLead: "建议领出 {card}，手中还有 {trumpCount} 张主牌{drawTrump}。",
    coachExpFollow: "建议用 {card} 跟 {leadSuit}，尝试赢下本墩。",
    coachExpTrump: "建议用 {card} 将吃，压过 {leadSuit}。",
    coachExpDuck: "建议垫出小牌 {card}{reason}。",
    dailyUnavailable: "本版本暂不提供今天的每日牌局——快速对局与教练模式仍可玩。",
    dailyReviewTitle: "每日复盘",
    dailyFocus: "今日焦点：{kind}。",
    kindOrder: "是否接受翻牌",
    kindPass: "先过后再叫的时机",
    kindAlone: "单打判断",
    dailyRecommended: "推荐叫法：{action}。",
    dailyYourCall: "你的叫法：{action}——{verdict}。",
    dailyMatched: "与推荐一致",
    dailyMissed: "与推荐不同",
    dailySimFacts: "本手模拟期望得分——接受 {order}、过 {pass}、单打 {alone}。",
    dailySimNote: "推荐来自固定预算的确定性 AI 推演（每个候选 {simulations} 次），是较强的启发式建议，并非完美牌技。",
    actOrder: "接受 {suit}",
    actOrderAlone: "接受 {suit} 并单打",
    actCall: "叫 {suit}",
    actCallAlone: "叫 {suit} 并单打",
    actPass: "过",
    dailyCallLabel: "叫法与推荐",
    callMatchedShort: "一致",
    callMissedShort: "不同",
    swingTitle: "关键转折",
    swingOwnEuchred: "第{n}手：你们叫主被反制——对方得 {points} 分。主牌单薄时的叫主代价最大。",
    swingOppEuchred: "第{n}手：你们反制了对方的叫主——我方得 {points} 分。防守弱叫主收益翻倍。",
    swingOwnMarch: "第{n}手：你们横扫五墩得 {points} 分。主牌够强又有控制，叫主就能打成横扫。",
    swingOppMarch: "第{n}手：对方横扫五墩得 {points} 分。留意主牌数量，别让局势失控。",
    swingOwnLoneMarch: "第{n}手：你的单打横扫得 {points} 分——牌桌上最大的一击。",
    swingOppLoneMarch: "第{n}手：对方单打横扫得 {points} 分。双 bower 加长套几乎无法阻挡。",
    swingOwnLoneFail: "第{n}手：你的单打被反制——对方得 {points} 分。单打需要近乎稳拿的墩。",
    swingOppLoneFail: "第{n}手：对方的单打被反制——我方得 {points} 分。惩罚贪婪的单打收益翻倍。",
    entryEyebrow: "第一次来到这张牌桌？",
    entryTitle: "选择你的入座方式",
    entryLead: "三种进入方式——任选其一即可开桌。",
    entryTutorialTitle: "快速教学",
    entryTutorialDesc: "第一次玩尤克牌？用一局固定牌，约 3 分钟学会叫主和出牌。",
    entryQuickTitle: "正常对局",
    entryQuickDesc: "直接开局——搭档双方先到 10 分获胜。",
    entryDailyTitle: "每日情景",
    entryDailyDesc: "今天的一手决策挑战。",
    tutorialTag: "教学",
    tutProgress: "第 {n} 步 / 共 6 步",
    tutNext: "下一步",
    tutExit: "退出教学",
    tutStartMatch: "开始正常对局",
    tut0Title: "认识你的搭档",
    tut0Body: "你的搭档是对面的北家 Riley，你们一起对抗 Jordan 和 Casey。目标是叫准主牌，并在五墩中至少拿下三墩。",
    tut1Title: "主牌与 Bower",
    tut1Body: "翻开的牌是主牌候选。主牌的 J（Right Bower）是全场最大；同色另一张 J（Left Bower）也算主牌，不再属于印刷花色。",
    tut2Title: "轮到你叫主",
    tut2Body: "可以「接受翻牌」让候选花色成为主牌，或「过」等待第二轮；「单打」会让搭档休战、冲击 4 分。任何选择都可以，看看它带来的结果。",
    tut2Passed: "你选择了过——叫主继续轮转。第二轮你可以叫任意其他花色。",
    tut3Title: "跟有效首出花色",
    tut3Body: "只要手里还有有效首出花色就必须跟；如果你是首家，任意牌都可出。合法牌已在手牌中高亮——记住 Left Bower 属于主牌。",
    tut3AfterOrder: "你接受了 {suit} 定主——你们至少要拿 3 墩，否则会被反制。",
    tut3AfterCall: "你叫了 {suit}——你们至少要拿 3 墩。",
    tut3AfterPass: "主牌由别家叫定——转为防守：阻止叫主队拿到 3 墩。",
    tut4Title: "赢墩判定",
    tut4Body: "有主牌时最大的主牌赢墩；没人出主牌时首出花色最大者赢墩。顶部栏可看到墩数和比分——先到 10 分的搭档获胜。",
    tut4Winner: "{seat} 赢下了第一墩。",
    tut5Title: "教学完成",
    tut5Body: "计分：叫主队赢 3–4 墩得 1 分，全赢得 2 分（march），单打全赢得 4 分；守方反制得 2 分。你已完成一次叫主和一次合法出牌——可以开始真正的对局了。",
    tutQuiet: "打完这一手——结束时会总结计分。",
  },
  ja: {
    brandSub: "ミッドナイト・パートナーシップ", quick: "クイック対戦", daily: "デイリーディール",
    coach: "コーチ", rules: "ルール", fullRules: "完全なルールと戦略", sound: "サウンド", on: "オン", off: "オフ", we: "自チーム",
    they: "相手", target: "目標", you: "あなた", cards: "枚", dealer: "ディーラー", maker: "メイカー",
    trump: "切り札", candidate: "切り札候補", noneYet: "未決定", round: "ラウンド",
    callRound1: "第1コールラウンド", callRound2: "第2コールラウンド", yourCall: "あなたのコールです。",
    pass: "パス", orderUp: "オーダーアップ", alone: "単独で行く", call: "コール",
    mustChoose: "ディーラーは必ずスートを選んでください。", discard: "捨て札を1枚選んでください。",
    yourTurn: "あなたの番です。合法なカードを選んでください。", thinking: "考えています…",
    collecting: "トリック完了", nextHand: "次のハンド", newMatch: "新しい対戦", continue: "保存した対戦を続ける",
    resume: "保存した対戦があります", coachInsight: "コーチの解説", showHint: "ヒントを表示", hideHint: "ヒントを隠す",
    handComplete: "ハンド終了", matchComplete: "対戦終了", share: "結果を共有", close: "閉じる", settings: "設定",
    reducedMotion: "動きを減らす", confirmPlay: "プレイ前に確認", clearData: "ローカルデータを消去",
    clearConfirm: "保存対戦、設定、ローカル統計をすべて消去しますか？元に戻せません。",
    restartConfirm: "この対戦を終了して最初から始めますか？",
    restartTitle: "新しい対戦を始めますか？", startOver: "最初から", cancel: "キャンセル", assisted: "アシストあり", unassisted: "Clean（アシストなし）",
    stats: "テーブル記録", wins: "勝利", games: "対戦", winRate: "勝率", hands: "ハンド",
    callSuccess: "コール成功率", euchresLabel: "ユーカー回数", aloneSuccess: "単独成功率",
    averageHands: "平均ハンド数", streak: "デイリー連続", latestDaily: "今日のデイリーディール",
    noDaily: "今日のデイリーディールは未完了です。", dailyWon: "勝利", dailyLost: "敗北",
    marginLabel: "得失点差", resultSummary: "対戦成績", playDaily: "デイリーディールをプレイ",
    heroEyebrow: "正しくコールし、3トリック取ろう。", gameModes: "ゲームモード",
    tableUtilities: "テーブル操作", language: "言語", playableTable: "操作できるユーカーテーブル",
    yourHand: "あなたの手札", scoreAria: "スコア", actionUnavailable: "現在その操作はできません。",
    coachOrder: "{suit}をオーダーアップ。十分な切り札の強さがあります。",
    coachPass: "今はパスして、第2コールラウンドの選択肢を残します。",
    coachCall: "{suit}をコール。選べる中で最も強い切り札スートです。",
    coachWait: "他の席の番は、公開されたトリックを確認しましょう。",
    coachFollow: "有効なリードスートに従い、{suit}の{rank}を出します。おすすめカードを強調しています。",
    coachBalance: "{rank}を出し、トリック獲得と強いカードの温存を両立します。",
    coachObserve: "コール順とパートナーの公開プレイを確認しましょう。",
    logStarted: "対戦開始", logRound2: "第2コールラウンド開始", logPassed: "{seat}がパス",
    logOrdered: "{seat}が{suit}をオーダーアップ{alone}", logCalled: "{seat}が{suit}をコール{alone}",
    logDiscarded: "{seat}が1枚捨てた", logPlayed: "{seat}が{card}をプレイ", logAlone: "（単独）",
    howTitle: "ユーカーの遊び方", howBody: "24枚、4席、2組のパートナーで競います。切り札を決め、5トリック中3つ以上を取り、先に10点を目指します。",
    bowerTitle: "バウアーは有効スートを変える",
    bowerBody: "切り札のJがRight Bower。同色のもう一方のJがLeft Bowerで、印刷スートではなく切り札として扱います。",
    callingTitle: "2回のコール", callingBody: "第1ラウンドはアップカードのスート。第2ラウンドは別のスート。全員がパスするとディーラーは必ず選びます。",
    scoreTitle: "固定スコア", scoreBody: "メイカーは3–4トリックで1点、5つで2点。ユーカーは守備側に2点。単独スイープは4点です。",
    strategyTitle: "パートナーを生かす戦略", strategyBody: "公開された切り札を数え、パートナーが勝っているなら理由なく上回らず、相手の切り札を引き出す時は強くリードします。",
    faqTitle: "よくある質問", faq1: "4人ユーカーですか？", faqa1: "はい。あなたと北が組み、西と東の、座席ごとに情報を分離したAIと対戦します。",
    faq2: "オフラインで遊べますか？", faqa2: "はい。初回アクセス後はゲーム本体とローカル保存をオフラインで利用できます。",
    faq3: "どのルール変種ですか？", faqa3: "24枚の標準パートナー戦とStick the Dealerです。Joker、32枚、地域ルールは含みません。",
    legalReason: "有効なリードスートを持っている間は、そのスートに従う必要があります。",
    leftBadge: "LEFT・切り札", rightBadge: "RIGHT・切り札", currentTrick: "現在のトリック",
    tricks: "トリック", gameLog: "ゲームログ", helpTitle: "ルールと操作",
    helpText: "2回のコールで切り札を決め、有効なリードスートに従います。Rightが最高、Leftが次で切り札扱い。矢印キーで移動、Spaceで選択、Enterでプレイ。",
    made: "メイカーが3または4トリック獲得。", march: "メイカーが5トリックを制覇。", euchred: "守備側がメイカーをユーカーしました。",
    "lone-made": "単独メイカーが3または4トリック獲得。", "lone-march": "単独スイープで4点。",
    teamWon: "あなたのチームが10点に到達しました。", teamLost: "相手チームが10点に到達しました。",
    dailyHandWon: "あなたのチームがデイリーのハンドを取りました。", dailyHandLost: "相手チームがデイリーのハンドを取りました。",
    difficulty: "AI の強さ",
    difficultyRelaxed: "カジュアル", difficultyStandard: "スタンダード", difficultySharp: "シャープ",
    difficultyRelaxedDesc: "寛容な相手：コールは慎重で、時々次善のカードを出します。",
    difficultyStandardDesc: "定番のバランス型テーブルAI。",
    difficultySharpDesc: "出た切り札を数え、最強切り札で引き出し、伏せられたカードも読みます。",
    bowerRight: "Right Bower", bowerLeft: "Left Bower", bowerIncl: "（{bowers}）",
    callFacts: "{suit}が切り札なら：手札に切り札{trumpCount}枚{bowers}。",
    suitFactShort: "切り札{trumpCount}枚{bowers}",
    legalReasonFollow: "{suit}に従ってください——まだ{count}枚持っています。",
    expOrder: "{suit}をオーダーアップ：切り札{trumpCount}枚{bowers}、オフスートのAが{offAces}枚、強さ{score}／しきい値{threshold}。",
    expCall: "{suit}をコール：切り札{trumpCount}枚{bowers}、オフスートのAが{offAces}枚、強さ{score}／しきい値{threshold}。",
    expPass: "{suit}をパス：切り札{trumpCount}枚{bowers}、強さ{score}はしきい値{threshold}未満。",
    expDiscard: "{suit}が切り札になったため、最弱のカードを捨てます。",
    expLead: "{card}をリード。手札の切り札は{trumpCount}枚{drawTrump}。",
    expLeadDraw: "。最強切り札が相手の切り札を引き出します",
    expFollow: "{leadSuit}に従い{card}を出し、このトリックを取りに行きます。",
    expTrump: "{card}で切り札を出し、{leadSuit}に勝ちます。",
    expDuck: "{card}を低く出します{reason}。",
    expDuckPartner: "——パートナーがすでにこのトリックを取っています",
    expDuckBeat: "——現在の最強カードには勝てません",
    coachExpOrder: "おすすめ——{suit}をオーダーアップ：切り札{trumpCount}枚{bowers}、オフスートのAが{offAces}枚、強さ{score}／しきい値{threshold}。",
    coachExpCall: "おすすめ——{suit}をコール：切り札{trumpCount}枚{bowers}、オフスートのAが{offAces}枚、強さ{score}／しきい値{threshold}。",
    coachExpPass: "おすすめ——{suit}をパス：切り札{trumpCount}枚{bowers}、強さ{score}はしきい値{threshold}未満。",
    coachExpDiscard: "おすすめ——{suit}が切り札になったら、最弱のカードを捨てましょう。",
    coachExpLead: "おすすめ——{card}をリード。手札の切り札は{trumpCount}枚{drawTrump}。",
    coachExpFollow: "おすすめ——{leadSuit}に従い{card}を出し、このトリックを取りに行きましょう。",
    coachExpTrump: "おすすめ——{card}で切り札を出し、{leadSuit}に勝ちましょう。",
    coachExpDuck: "おすすめ——{card}を低く出しましょう{reason}。",
    dailyUnavailable: "このバージョンでは今日のデイリーディールを利用できません。クイック対戦とコーチはプレイできます。",
    dailyReviewTitle: "デイリー振り返り",
    dailyFocus: "今日のテーマ：{kind}。",
    kindOrder: "オーダーアップの判断",
    kindPass: "パスして後でコールする判断",
    kindAlone: "単独の判断",
    dailyRecommended: "推奨コール：{action}。",
    dailyYourCall: "あなたのコール：{action} — {verdict}。",
    dailyMatched: "推奨と一致",
    dailyMissed: "推奨と相違",
    dailySimFacts: "このハンドのシミュレーション期待点 — オーダー {order}、パス {pass}、単独 {alone}。",
    dailySimNote: "推奨は固定予算の決定論的AIシミュレーション（各候補{simulations}回）によるもので、完全な最適手ではありません。",
    actOrder: "{suit}をオーダーアップ",
    actOrderAlone: "{suit}を単独でオーダーアップ",
    actCall: "{suit}をコール",
    actCallAlone: "{suit}を単独でコール",
    actPass: "パス",
    dailyCallLabel: "コールと推奨",
    callMatchedShort: "一致",
    callMissedShort: "相違",
    swingTitle: "勝負の分かれ目",
    swingOwnEuchred: "第{n}ハンド：あなたのコールがユーカーされ、相手に{points}点。薄い切り札のコールが最も高くつきます。",
    swingOppEuchred: "第{n}ハンド：相手のコールをユーカーし、{points}点獲得。弱いコールの守備は2点の価値です。",
    swingOwnMarch: "第{n}ハンド：5トリック制覇で{points}点。強い切り札と主導権がマーチを生みます。",
    swingOppMarch: "第{n}ハンド：相手が5トリック制覇で{points}点。切り札の枚数を数えておきましょう。",
    swingOwnLoneMarch: "第{n}ハンド：あなたの単独スイープで{points}点——テーブル最大のプレーです。",
    swingOppLoneMarch: "第{n}ハンド：相手の単独スイープで{points}点。両バウアーと長いスートは止められません。",
    swingOwnLoneFail: "第{n}ハンド：あなたの単独コールがユーカーされ、相手に{points}点。単独にはほぼ確実なトリックが必要です。",
    swingOppLoneFail: "第{n}ハンド：相手の単独コールをユーカーし、{points}点獲得。欲張った単独への反制は2点です。",
    entryEyebrow: "このテーブルは初めてですか？",
    entryTitle: "座り方を選んでください",
    entryLead: "3つの入り方——どれか一つでテーブルが開きます。",
    entryTutorialTitle: "クイックチュートリアル",
    entryTutorialDesc: "ユーカーが初めてなら、固定の練習ハンドで約3分。コールとプレイを学べます。",
    entryQuickTitle: "通常対戦",
    entryQuickDesc: "すぐに着席——先に10点のパートナーが勝ちます。",
    entryDailyTitle: "デイリーディール",
    entryDailyDesc: "今日の一手の判断に挑戦。",
    tutorialTag: "チュートリアル",
    tutProgress: "ステップ {n}/6",
    tutNext: "次へ",
    tutExit: "チュートリアルを終了",
    tutStartMatch: "通常対戦を始める",
    tut0Title: "パートナーの紹介",
    tut0Body: "向かいの北・Rileyがあなたのパートナー。二人でJordanとCaseyに挑みます。切り札を見極め、5トリック中3つ以上を取りましょう。",
    tut1Title: "切り札とバウアー",
    tut1Body: "表向きのカードが切り札候補。切り札のJ（Right Bower）が最強で、同色のもう一枚のJ（Left Bower）も印刷スートではなく切り札として扱います。",
    tut2Title: "コールしましょう",
    tut2Body: "「オーダーアップ」で候補スートを切り札にするか、「パス」で第2ラウンドへ。「単独」はパートナーを休ませて4点を狙えます。どれを選んでも大丈夫です。",
    tut2Passed: "パスしました——コールは次へ回ります。第2ラウンドでは別のスートを選べます。",
    tut3Title: "リードスートに従う",
    tut3Body: "有効なリードスートを持っている間はそれに従います。リードする番ならどのカードも出せます。出せるカードは手札で強調されます——Left Bowerは切り札扱いです。",
    tut3AfterOrder: "{suit}をオーダーアップ——3トリック取れないとユーカーされます。",
    tut3AfterCall: "{suit}をコール——3トリック以上が必要です。",
    tut3AfterPass: "切り札は他の席が決めました——メイカーの3トリックを阻みましょう。",
    tut4Title: "トリックの勝敗",
    tut4Body: "切り札が出ていれば最強の切り札、なければリードスートの最大がトリックを取ります。上部のバーでトリック数とスコアを確認——先に10点のチームが勝利です。",
    tut4Winner: "{seat}が最初のトリックを取りました。",
    tut5Title: "チュートリアル完了",
    tut5Body: "スコア：メイカーは3–4トリックで1点、5つ（マーチ）で2点、単独スイープで4点。ユーカーは守備に2点。コールと合法なプレイができました——実戦へ進みましょう。",
    tutQuiet: "このハンドを最後まで——終了時にスコアをまとめます。",
  },
} as const;

export function localeFromPath(path = location.pathname): Locale {
  if (path === "/zh" || path.startsWith("/zh/")) return "zh";
  if (path === "/ja" || path.startsWith("/ja/")) return "ja";
  return "en";
}

export function t(locale: Locale, key: keyof typeof translations.en): string {
  return translations[locale][key] ?? translations.en[key];
}

export const suitText: Record<Locale, Record<Suit, string>> = {
  en: { clubs: "Clubs", diamonds: "Diamonds", hearts: "Hearts", spades: "Spades" },
  zh: { clubs: "梅花", diamonds: "方块", hearts: "红桃", spades: "黑桃" },
  ja: { clubs: "クラブ", diamonds: "ダイヤ", hearts: "ハート", spades: "スペード" },
};

export const suitSymbol: Record<Suit, string> = {
  clubs: "♣",
  diamonds: "♦",
  hearts: "♥",
  spades: "♠",
};

export const seatText: Record<Locale, string[]> = {
  en: ["You", "West · Jordan (AI)", "North · Riley (AI)", "East · Casey (AI)"],
  zh: ["你", "西家 · Jordan（AI）", "北家 · Riley（AI）", "东家 · Casey（AI）"],
  ja: ["あなた", "西・Jordan（AI）", "北・Riley（AI）", "東・Casey（AI）"],
};

export function fill(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, value), template);
}

/** Suffix listing held bowers, e.g. ", incl. Right Bower"; empty without any. */
export function bowerSuffix(facts: { hasRight: boolean; hasLeft: boolean }, locale: Locale): string {
  const labels = [
    facts.hasRight ? t(locale, "bowerRight") : "",
    facts.hasLeft ? t(locale, "bowerLeft") : "",
  ].filter(Boolean);
  return labels.length ? fill(t(locale, "bowerIncl"), { bowers: labels.join(" + ") }) : "";
}

function cardLabelFromId(cardId: string, locale: Locale): string {
  const [suit, rank] = cardId.split("-");
  return suit in suitText[locale] ? `${rank} ${suitText[locale][suit as Suit]}` : cardId;
}

/**
 * Whether an explanation is a forward-looking recommendation (Coach panel,
 * before the human acts) or a completed event note (after an AI action has
 * been applied to state/history). Callers always know which they are showing
 * and pass it explicitly; the two contexts never share a past-tense main
 * clause.
 */
export type ExplanationContext = "recommendation" | "event";

export function localizeExplanation(explanation: AiExplanation, locale: Locale, context: ExplanationContext): string {
  if (context !== "recommendation" && context !== "event") {
    throw new Error(`Unknown explanation context: ${String(context)}`);
  }
  const recommendation = context === "recommendation";
  if (explanation.kind === "discard") {
    return fill(t(locale, recommendation ? "coachExpDiscard" : "expDiscard"), { suit: suitText[locale][explanation.trump] });
  }
  if (explanation.kind === "lead") {
    return fill(t(locale, recommendation ? "coachExpLead" : "expLead"), {
      card: cardLabelFromId(explanation.cardId, locale),
      trumpCount: String(explanation.trumpCount),
      drawTrump: explanation.drawTrump ? t(locale, "expLeadDraw") : "",
    });
  }
  if (explanation.kind === "follow" || explanation.kind === "trump" || explanation.kind === "duck") {
    const card = cardLabelFromId(explanation.cardId, locale);
    const leadSuit = suitText[locale][explanation.leadSuit];
    if (explanation.kind === "follow") return fill(t(locale, recommendation ? "coachExpFollow" : "expFollow"), { card, leadSuit });
    if (explanation.kind === "trump") return fill(t(locale, recommendation ? "coachExpTrump" : "expTrump"), { card, leadSuit });
    return fill(t(locale, recommendation ? "coachExpDuck" : "expDuck"), {
      card,
      reason: t(locale, explanation.partnerWinning ? "expDuckPartner" : "expDuckBeat"),
    });
  }
  if (explanation.kind === "order" || explanation.kind === "call" || explanation.kind === "pass") {
    const suit = explanation.suit ? suitText[locale][explanation.suit] : "";
    const values = {
      suit,
      trumpCount: String(explanation.trumpCount),
      bowers: bowerSuffix(explanation, locale),
      offAces: String(explanation.offAces),
      threshold: String(explanation.threshold),
      score: String(explanation.score),
    };
    if (explanation.kind === "order") return fill(t(locale, recommendation ? "coachExpOrder" : "expOrder"), values);
    if (explanation.kind === "call") return fill(t(locale, recommendation ? "coachExpCall" : "expCall"), values);
    return fill(t(locale, recommendation ? "coachExpPass" : "expPass"), values);
  }
  throw new Error("unreachable explanation kind");
}
